import React, { useMemo, useState } from 'react';

import {
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  TableSortLabel,
  Box,
} from '@material-ui/core';

import { SummaryRow } from '../types';
import ActionChip from './action-chip';

type Order = 'asc' | 'desc';

interface Props {
  data: SummaryRow[];
}

const format = (value: number) => Intl.NumberFormat('en-US').format(value);

const SummaryTable = ({ data }: Props) => {
  const [search, setSearch] = useState('');

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [order, setOrder] = useState<Order>('desc');

  const [orderBy, setOrderBy] = useState<'month' | 'week'>('month');

  const filtered = useMemo(() => {
    return data.filter(item => item.fieldName.toLowerCase().includes(search.toLowerCase()));
  }, [data, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const first = orderBy === 'month' ? a.rows[3].month : a.rows[3].week;

      const second = orderBy === 'month' ? b.rows[3].month : b.rows[3].week;

      return order === 'asc' ? first - second : second - first;
    });
  }, [filtered, order, orderBy]);

  const rows = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const requestSort = (property: 'week' | 'month') => {
    const asc = property === orderBy && order === 'asc';

    setOrder(asc ? 'desc' : 'asc');

    setOrderBy(property);
  };

  return (
    <Card
      elevation={3}
      style={{
        borderRadius: 18,
      }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography
            variant="h6"
            style={{
              fontWeight: 700,
            }}>
            Summary
          </Typography>

          <TextField
            size="small"
            variant="outlined"
            placeholder="Search field..."
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(0); // Reset to first page
            }}
            style={{
              width: 300,
            }}
          />
        </Box>

        <TableContainer
          style={{
            maxHeight: 650,
          }}>
          <Table stickyHeader size="small">
            <TableHead >
              <TableRow  >
                <TableCell rowSpan={2}>Field</TableCell>

                <TableCell rowSpan={2} align="center">
                  Action
                </TableCell>

                <TableCell colSpan={7} align="center">
                  Last 7 Days
                </TableCell>

                <TableCell rowSpan={2} align="center">
                  <TableSortLabel
                    active={orderBy === 'week'}
                    direction={order}
                    onClick={() => requestSort('week')}>
                    Week Total
                  </TableSortLabel>
                </TableCell>

                <TableCell rowSpan={2} align="center">
                  <TableSortLabel
                    active={orderBy === 'month'}
                    direction={order}
                    onClick={() => requestSort('month')}>
                    Month Total
                  </TableSortLabel>
                </TableCell>
              </TableRow>

              <TableRow>
                {['T0', 'T1', 'T2', 'T3', 'T4', 'T5', 'T6'].map(day => (
                  <TableCell key={day} align="center">
                    {day}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((field, index) => (
                <React.Fragment key={field.fieldName}>
                  {field.rows.map((row, rowIndex) => {
                    const isTotal = row.action === 'Total';

                    return (
                      <TableRow
                        hover
                        key={row.action}
                        style={{
                          background: index % 2 === 0 ? '#fff' : '#fafafa',
                        }}>
                        {rowIndex === 0 && (
                          <TableCell
                            rowSpan={4}
                            style={{
                              fontWeight: 700,
                              minWidth: 220,
                              borderRight: '1px solid #eee',
                              verticalAlign: 'middle',
                            }}>
                            {field.fieldName}
                          </TableCell>
                        )}

                        <TableCell>
                          <ActionChip action={row.action} />
                        </TableCell>

                        <TableCell align="center">{format(row.t0)}</TableCell>

                        <TableCell align="center">{format(row.t1)}</TableCell>

                        <TableCell align="center">{format(row.t2)}</TableCell>

                        <TableCell align="center">{format(row.t3)}</TableCell>

                        <TableCell align="center">{format(row.t4)}</TableCell>

                        <TableCell align="center">{format(row.t5)}</TableCell>

                        <TableCell align="center">{format(row.t6)}</TableCell>

                        <TableCell
                          align="center"
                          style={{
                            fontWeight: isTotal ? 700 : 500,
                            color: isTotal ? '#4F46E5' : undefined,
                          }}>
                          {format(row.week)}
                        </TableCell>

                        <TableCell
                          align="center"
                          style={{
                            fontWeight: isTotal ? 700 : 500,
                            color: isTotal ? '#4F46E5' : undefined,
                          }}>
                          {format(row.month)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 10, 20]}
          rowsPerPage={rowsPerPage}
          page={page}
          count={sorted.length}
          onChangePage={(e, newPage) => setPage(newPage)}
          onChangeRowsPerPage={e => {
            setRowsPerPage(parseInt(e.target.value, 10));

            setPage(0);
          }}
        />
      </CardContent>
    </Card>
  );
};

export default SummaryTable;
